import React, { useState, useEffect } from 'react';
import { SkeletonMachinesCustomers } from "../components/SkeletonLoader";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import FadeIn from "../components/FadeIn";
import { Link, useSearchParams } from 'react-router-dom';
import { Package, Search, Download, Filter, Eye, Edit2 } from 'lucide-react';
import { MACHINE_TYPES, MACHINE_STATUSES, MACHINE_STATUS_LABELS, MACHINE_STATUS_COLORS } from '../constants';

const Machines = () => {
  const [searchParams] = useSearchParams();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useDelayedLoading(loading, 250);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState(searchParams.get('type') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [exporting, setExporting] = useState(false);
  const [sortBy, setSortBy] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch('/api/machines', { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setMachines(json.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
    setLoading(false);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetch('/api/machines/export', { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'machines.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
    setExporting(false);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterType('');
    setFilterStatus('');
    setSortBy('');
  };

  const getSortValue = (m, key) => {
    switch (key) {
      case 'serial': return m.serial_number || '';
      case 'customer': return (m.customer_name || '').toLowerCase();
      case 'contract': return m.contract_signed_at ? new Date(m.contract_signed_at).getTime() : -Infinity;
      case 'handover': {
        const d = m.delivered_at || m.contract_signed_at;
        return d ? new Date(d).getTime() : -Infinity;
      }
      default: return 0;
    }
  };

  const filteredMachines = machines.filter((m) => {
    const matchesSearch = !search || 
      m.serial_number?.toLowerCase().includes(search.toLowerCase()) || 
      m.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      m.model_type?.toLowerCase().includes(search.toLowerCase());
    const matchesType = !filterType || m.model_type === filterType;
    const matchesStatus = !filterStatus || m.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  }).sort((a, b) => {
    if (!sortBy) return 0;
    const va = getSortValue(a, sortBy);
    const vb = getSortValue(b, sortBy);
    if (va === vb) return 0;
    if (va === -Infinity) return 1;
    if (vb === -Infinity) return -1;
    return va > vb ? 1 : -1;
  });

  if (showLoading) return <SkeletonMachinesCustomers />;

  const selectStyle = { padding: '10px 16px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px', backgroundColor: '#FFF', minWidth: '150px' };

  const hasActiveFilters = search || filterType || filterStatus;

  return (
    <FadeIn show={!showLoading}>
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#333', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Package color="#E53935" /> Trenažéry
        </h1>
        <button
          onClick={handleExportCSV}
          disabled={exporting}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#E53935',
            color: '#FFF',
            border: 'none',
            borderRadius: '8px',
            cursor: exporting ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            opacity: exporting ? 0.7 : 1
          }}
        >
          <Download size={16} />
          {exporting ? 'Exportuji...' : 'Export CSV'}
        </button>
      </div>

      <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter size={20} color="#E53935" />
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#333', margin: 0 }}>Filtry a vyhledávání</h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                marginLeft: 'auto',
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: '#F5F5F5',
                color: '#666',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Vymazat filtry
            </button>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'end' }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#333', marginBottom: '6px' }}>
              Vyhledávání
            </label>
            <div style={{ position: 'relative' }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }} size={20} />
              <input 
                type="text" 
                placeholder="Sériové číslo, zákazník, typ..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px 10px 10px 44px', 
                  border: '1px solid #E0E0E0', 
                  borderRadius: '8px', 
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
              />
            </div>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#333', marginBottom: '6px' }}>
              Typ
            </label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
              <option value="">Všechny typy</option>
              {MACHINE_TYPES.map(t => <option key={t.value} value={t.value}>{t.shortLabel}</option>)}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#333', marginBottom: '6px' }}>
              Stav
            </label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
              <option value="">Všechny stavy</option>
              {MACHINE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#333', marginBottom: '6px' }}>
              Řadit podle
            </label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={selectStyle}>
              <option value="">Výchozí</option>
              <option value="serial">Sériové číslo</option>
              <option value="customer">Zákazník</option>
              <option value="contract">Podpis smlouvy</option>
              <option value="handover">Datum předání (instalace)</option>
            </select>
          </div>
        </div>
        
        {hasActiveFilters && (
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
            Zobrazeno {filteredMachines.length} z {machines.length} trenažérů
          </div>
        )}
      </div>

      <div style={{ backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#F5F5F5', borderBottom: '2px solid #E0E0E0' }}>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666', width: '120px' }}>Sériové číslo</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666', width: '160px' }}>Typ</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666' }}>Zákazník</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666' }}>Podpis smlouvy</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666' }}>Instalace</th>
              <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666' }}>Stav</th>
            </tr>
          </thead>
          <tbody>
            {filteredMachines.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '32px', textAlign: 'center', color: '#999' }}>
                  {hasActiveFilters ? 'Žádné trenažéry neodpovídají filtrům' : 'Žádné trenažéry'}
                </td>
              </tr>
            ) : (
              filteredMachines.map((machine) => (
                <tr key={machine.id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                  <td style={{ padding: '16px' }}>
                    <Link to={`/machines/${machine.id}`} style={{ color: '#E53935', textDecoration: 'none', fontWeight: 500, fontSize: '14px' }}>
                      {machine.serial_number}
                    </Link>
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#333' }}>{machine.model_type}</td>
                  <td style={{ padding: '16px', fontSize: '14px' }}>
                    {machine.customer_id ? (
                      <Link to={`/customers/${machine.customer_id}`} style={{ color: '#1976D2', textDecoration: 'none' }}>
                        {machine.customer_name}
                      </Link>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                    {machine.contract_signed_at ? new Date(machine.contract_signed_at).toLocaleDateString('cs-CZ') : '-'}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666' }}>
                    {machine.delivered_at ? new Date(machine.delivered_at).toLocaleDateString('cs-CZ') : '-'}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: MACHINE_STATUS_COLORS[machine.status]?.bg || '#F5F5F5',
                      color: MACHINE_STATUS_COLORS[machine.status]?.color || '#666'
                    }}>
                      {MACHINE_STATUS_LABELS[machine.status] || machine.status || 'Neznámý'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
    </FadeIn>
  );
};

export default Machines;
